/**
 * 统一手风琴组件
 * 支持两种模式：
 * 1. details/summary 模式 - 渐进增强，使用原生 HTML 元素
 * 2. data-* 属性模式 - 自定义结构，更多控制
 *
 * 配置属性：
 * - data-allow-multiple: 是否允许同时打开多项
 * - data-open-first: 默认打开第一项
 * - data-duration: 动画时长(ms)
 */

// Web Component 实现 - 用于 details/summary 模式
if (!customElements.get('accordion-component')) {
  customElements.define(
    'accordion-component',
    class AccordionComponent extends HTMLElement {
      constructor() {
        super();
        this.allowMultiple = this.dataset.allowMultiple === 'true';
        this.openFirst = this.dataset.openFirst === 'true';
        this.detailsElements = this.querySelectorAll('details');
      }

      connectedCallback() {
        if (this._initialized) return;
        this._initialized = true;

        this.initDetails();
        this.initKeyboardNav();
      }

      disconnectedCallback() {
        this._initialized = false;
      }

      initDetails() {
        // 默认打开第一项
        if (this.openFirst && this.detailsElements.length > 0) {
          const firstDetails = this.detailsElements[0];
          if (!firstDetails.hasAttribute('open')) {
            firstDetails.setAttribute('open', '');
          }
        }

        // 绑定 toggle 事件
        this.detailsElements.forEach((details) => {
          details.addEventListener('toggle', (e) => {
            if (e.target.open && !this.allowMultiple) {
              this.closeOthers(e.target);
            }
            // 触发自定义事件
            this.dispatchEvent(
              new CustomEvent('accordion:toggle', {
                bubbles: true,
                detail: {
                  details: e.target,
                  open: e.target.open
                }
              })
            );
          });
        });
      }

      initKeyboardNav() {
        const summaries = Array.from(this.querySelectorAll('summary'));
        
        summaries.forEach((summary, index) => {
          summary.addEventListener('keydown', (e) => {
            let targetIndex = index;

            switch (e.key) {
              case 'ArrowDown':
                e.preventDefault();
                targetIndex = (index + 1) % summaries.length;
                break;
              case 'ArrowUp':
                e.preventDefault();
                targetIndex = (index - 1 + summaries.length) % summaries.length;
                break;
              case 'Home':
                e.preventDefault();
                targetIndex = 0;
                break;
              case 'End':
                e.preventDefault();
                targetIndex = summaries.length - 1;
                break;
              default:
                return;
            }

            summaries[targetIndex]?.focus();
          });
        });
      }

      closeOthers(current) {
        this.detailsElements.forEach((details) => {
          if (details !== current && details.open) {
            details.removeAttribute('open');
          }
        });
      }

      // 公共 API
      open(index) {
        const details = this.detailsElements[index];
        if (details) details.setAttribute('open', '');
      }

      close(index) {
        const details = this.detailsElements[index];
        if (details) details.removeAttribute('open');
      }

      closeAll() {
        this.detailsElements.forEach((details) => details.removeAttribute('open'));
      }

      openAll() {
        if (this.allowMultiple) {
          this.detailsElements.forEach((details) => details.setAttribute('open', ''));
        }
      }
    }
  );
}

// 类实现 - 用于 data-* 属性模式（更多控制）
class Accordion {
  constructor(element, options = {}) {
    this.accordion = element;
    this.options = {
      allowMultiple: options.allowMultiple || false,
      duration: options.duration || 300,
      onOpen: options.onOpen || null,
      onClose: options.onClose || null
    };

    this.items = Array.from(this.accordion.querySelectorAll('[data-accordion-item]'));
    this.triggers = Array.from(this.accordion.querySelectorAll('[data-accordion-trigger]'));
    this.panels = Array.from(this.accordion.querySelectorAll('[data-accordion-panel]'));

    this.init();
  }

  init() {
    // 绑定点击事件
    this.triggers.forEach((trigger, index) => {
      trigger.addEventListener('click', () => this.toggle(index));
      
      // 键盘导航
      trigger.addEventListener('keydown', (e) => this.handleKeyboard(e, index));
    });

    // 初始化面板高度
    this.panels.forEach(panel => {
      if (!panel.hidden) {
        panel.style.maxHeight = panel.scrollHeight + 'px';
      } else {
        panel.style.maxHeight = '0';
      }
    });
  }

  toggle(index) {
    const item = this.items[index];
    const trigger = this.triggers[index];
    const panel = this.panels[index];
    const isActive = item.classList.contains('is-active');

    if (isActive) {
      this.close(index);
    } else {
      // 如果不允许多个展开，先关闭其他项
      if (!this.options.allowMultiple) {
        this.items.forEach((otherItem, otherIndex) => {
          if (otherIndex !== index && otherItem.classList.contains('is-active')) {
            this.close(otherIndex);
          }
        });
      }
      this.open(index);
    }
  }

  open(index) {
    const item = this.items[index];
    const trigger = this.triggers[index];
    const panel = this.panels[index];

    // 更新状态
    item.classList.add('is-active');
    trigger.setAttribute('aria-expanded', 'true');
    panel.hidden = false;

    // 平滑展开动画
    requestAnimationFrame(() => {
      panel.style.maxHeight = panel.scrollHeight + 'px';
    });

    // 触发自定义事件
    this.accordion.dispatchEvent(
      new CustomEvent('accordion:toggle', {
        bubbles: true,
        detail: { index, item, open: true }
      })
    );

    // 回调
    if (this.options.onOpen) {
      this.options.onOpen(index, item);
    }
  }

  close(index) {
    const item = this.items[index];
    const trigger = this.triggers[index];
    const panel = this.panels[index];

    // 更新状态
    item.classList.remove('is-active');
    trigger.setAttribute('aria-expanded', 'false');

    // 平滑收起动画
    panel.style.maxHeight = '0';

    // 动画结束后隐藏
    setTimeout(() => {
      if (!item.classList.contains('is-active')) {
        panel.hidden = true;
      }
    }, this.options.duration);

    // 触发自定义事件
    this.accordion.dispatchEvent(
      new CustomEvent('accordion:toggle', {
        bubbles: true,
        detail: { index, item, open: false }
      })
    );

    // 回调
    if (this.options.onClose) {
      this.options.onClose(index, item);
    }
  }

  handleKeyboard(event, index) {
    const { key } = event;
    let targetIndex = index;

    switch (key) {
      case 'ArrowDown':
        event.preventDefault();
        targetIndex = (index + 1) % this.triggers.length;
        this.triggers[targetIndex].focus();
        break;

      case 'ArrowUp':
        event.preventDefault();
        targetIndex = (index - 1 + this.triggers.length) % this.triggers.length;
        this.triggers[targetIndex].focus();
        break;

      case 'Home':
        event.preventDefault();
        this.triggers[0].focus();
        break;

      case 'End':
        event.preventDefault();
        this.triggers[this.triggers.length - 1].focus();
        break;
    }
  }

  openAll() {
    this.items.forEach((_, index) => this.open(index));
  }

  closeAll() {
    this.items.forEach((_, index) => this.close(index));
  }
}

// 自动初始化 data-* 模式
document.addEventListener('DOMContentLoaded', () => {
  const accordions = document.querySelectorAll('[data-accordion]:not(accordion-component)');
  
  accordions.forEach(accordion => {
    // 从 data 属性读取配置
    const allowMultiple = accordion.dataset.allowMultiple === 'true';
    const duration = parseInt(accordion.dataset.duration) || 300;

    new Accordion(accordion, {
      allowMultiple,
      duration
    });
  });
});

// Shopify Section 重新加载时重新初始化
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    // 初始化 accordion-component（Web Component 会自动初始化）
    const dataAccordions = event.target.querySelectorAll('[data-accordion]:not(accordion-component)');
    dataAccordions.forEach(accordion => {
      new Accordion(accordion);
    });
  });
}

// 导出供外部使用
window.Accordion = Accordion;
